import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Select from "react-select";
import Swal from "sweetalert2";
import useRequiredFields from "../../../../hooks/useRequiredFields";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import { BudgetPlanStyles, money, num } from "./BudgetPlanShared";

const emptyLine = () => ({ chart_of_account_id: "", purpose: "", amount: "" });

const CreateBudgetPlan = () => {
  const navigate = useNavigate();
  const [inputs, setInputs] = useState({});
  const [error, setError] = useState({});
  const [totalAmount, setTotalAmount] = useState(0);
  const [list, setList] = useState([emptyLine()]);
  const [chartOfAccount, setChartOfAccount] = useState([]);
  const [saving, setSaving] = useState(false);
  const { checkRequiredFields } = useRequiredFields();

  const handleChange = (e) => {
    const name = e.target.name;
    const value = e.target.value;
    setInputs((values) => ({ ...values, [name]: value }));
  };

  const recalculateTotal = (rows) => {
    const total = rows.reduce(
      (sum, item) => sum + (num(item.amount) > 0 ? num(item.amount) : 0),
      0
    );
    setTotalAmount(Number(total.toFixed(2)));
  };

  const handleInputChange = (e, index) => {
    const { name, value } = e.target;
    const inputInfo = [...list];

    inputInfo[index][name] = value;
    setList(inputInfo);

    if (name === "amount") {
      recalculateTotal(inputInfo);
    }
  };

  // One account per plan: the database enforces it, so the form warns first.
  const handleAccountChange = (selectedOption, index) => {
    const value = selectedOption ? selectedOption.value : "";
    const alreadyUsed = list.some(
      (item, itemIndex) =>
        itemIndex !== index &&
        String(item.chart_of_account_id) === String(value)
    );

    if (alreadyUsed) {
      Swal.fire(
        "Already budgeted",
        "This chart of account is already on another line of this plan.",
        "warning"
      );
      return;
    }

    const inputInfo = [...list];
    inputInfo[index].chart_of_account_id = value;

    // The account name is a sensible default for the purpose text.
    if (!inputInfo[index].purpose && selectedOption) {
      inputInfo[index].purpose = selectedOption.label;
    }

    setList(inputInfo);
  };

  const displayError = (fieldName) => {
    if (error && error[fieldName]) {
      return <div className="text-danger small mg-t-4">{error[fieldName]}</div>;
    }
    return null;
  };

  const addMoreList = () => {
    setList([...list, emptyLine()]);
  };

  const removeList = (index) => {
    Swal.fire({
      title: "Remove this line?",
      text: "The budget line will be dropped from this plan.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#2a2962",
      cancelButtonColor: "#e42320",
      confirmButtonText: "Yes, remove it",
    }).then((result) => {
      if (result.isConfirmed) {
        const inputInfo = [...list];
        inputInfo.splice(index, 1);
        setList(inputInfo);
        recalculateTotal(inputInfo);
      }
    });
  };

  const getCurrentDateInput = () => {
    const dateObj = new Date();

    // get the month in this format of 04, the same for months
    const month = ("0" + (dateObj.getMonth() + 1)).slice(-2);
    const day = ("0" + dateObj.getDate()).slice(-2);
    const year = dateObj.getFullYear();

    const shortDate = `${year}-${month}-${day}`;

    setInputs((values) => ({
      ...values,
      start_date: shortDate,
      end_date: shortDate,
    }));
  };

  useEffect(() => {
    getCurrentDateInput();

    Http.get("/chart-of-account-code-2").then((res) => {
      setChartOfAccount(res.data);
    });
  }, []);

  const selectedAccount = (row) =>
    chartOfAccount.find(
      (option) => String(option.value) === String(row.chart_of_account_id)
    ) || null;

  const formSubmit = () => {
    checkRequiredFields();

    const missingAccount = list.some((item) => !item.chart_of_account_id);
    if (missingAccount) {
      Swal.fire(
        "Chart of account required",
        "Every budget line needs an account.",
        "warning"
      );
      return;
    }

    setSaving(true);
    setError({});

    Http.post("/budget-plan", { ...inputs, total_amount: totalAmount, list })
      .then((res) => {
        navigate("/budget-plan", {
          state: { message: res.data.message, type: "success" },
        });
      })
      .catch(function (e) {
        setSaving(false);
        setError(e.response?.data?.errors || {});
        Swal.fire(
          "Not saved",
          e.response?.data?.message || "Please try again.",
          "warning"
        );
      });
  };

  return (
    <div>
      <IsLogin></IsLogin>
      <div className="am-mainpanel">
        <BudgetPlanStyles />
        <div className="am-pagetitle">
          <div>
            <h5 className="am-title mb-1">Create Budget Plan</h5>
            <p className="text-muted mb-0 tx-12">
              Allocate a budget per chart of account for a date range
            </p>
          </div>
          <Link to="/budget-plan" className="btn btn-outline-secondary">
            <i className="fa fa-arrow-left mg-r-6"></i>Back to List
          </Link>
        </div>

        <div className="am-pagebody">
          <div className="bp-notice">
            <i className="fa fa-info-circle"></i>
            <span>
              A new plan is saved as a <strong>draft</strong>. Submit it from the
              list once the lines are final, and it becomes spendable after
              approval.
            </span>
          </div>

          <div className="bp-panel">
            <div className="bp-panel-head">
              <span>
                <i className="fa fa-calendar-o me-2"></i>Plan Information
              </span>
            </div>
            <div className="bp-panel-body">
              <div className="row">
                <div className="col-lg-3">
                  <label>
                    Start Date <i className="fa fa-star required"></i>
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    name="start_date"
                    value={inputs.start_date || ""}
                    onChange={handleChange}
                    data-required
                  />
                  {displayError("start_date")}
                </div>
                <div className="col-lg-3 mg-t-10 mg-lg-t-0">
                  <label>
                    End Date <i className="fa fa-star required"></i>
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    name="end_date"
                    value={inputs.end_date || ""}
                    onChange={handleChange}
                    data-required
                  />
                  {displayError("end_date")}
                </div>
                <div className="col-lg-6 mg-t-10 mg-lg-t-0">
                  <label>Remarks</label>
                  <textarea
                    className="form-control"
                    name="message"
                    rows="1"
                    value={inputs.message || ""}
                    onChange={handleChange}
                    placeholder="What this plan covers"
                  />
                  {displayError("message")}
                </div>
              </div>
            </div>
          </div>

          <div className="bp-panel">
            <div className="bp-panel-head">
              <span>
                <i className="fa fa-list-ul me-2"></i>Budget Lines
              </span>
              <span className="bp-head-note">
                {list.length} {list.length === 1 ? "line" : "lines"} &middot;
                one chart of account per line
              </span>
            </div>
            <div className="table-responsive">
              <table className="table bp-line-table">
                <thead>
                  <tr>
                    <th style={{ width: "44px" }}>#</th>
                    <th style={{ width: "38%" }}>
                      Chart of Account <i className="fa fa-star required"></i>
                    </th>
                    <th style={{ width: "32%" }}>Purpose</th>
                    <th className="bp-num" style={{ width: "20%" }}>
                      Amount
                    </th>
                    <th style={{ width: "60px" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {Array.from(list).map((x, i) => (
                    <tr key={i}>
                      <td>
                        <span className="bp-row-index">{i + 1}</span>
                      </td>
                      <td>
                        <Select
                          className="basic-single"
                          classNamePrefix="select"
                          name="chart_of_account_id"
                          options={chartOfAccount}
                          value={selectedAccount(x)}
                          onChange={(selectedOption) =>
                            handleAccountChange(selectedOption, i)
                          }
                          placeholder="Select Chart of Account"
                          menuPortalTarget={document.body}
                          styles={{
                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                          }}
                        />
                        {displayError("list." + i + ".chart_of_account_id")}
                      </td>
                      <td>
                        <input
                          type="text"
                          className="form-control"
                          name="purpose"
                          value={x.purpose || ""}
                          onChange={(e) => handleInputChange(e, i)}
                          placeholder="Input Purpose"
                          data-required
                        />
                        {displayError("list." + i + ".purpose")}
                      </td>
                      <td>
                        <input
                          type="number"
                          className="form-control bp-num"
                          name="amount"
                          value={x.amount || ""}
                          onChange={(e) => handleInputChange(e, i)}
                          placeholder="0.00"
                          data-required
                        />
                        {displayError("list." + i + ".amount")}
                      </td>
                      <td className="text-center">
                        {list.length > 1 && (
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => removeList(i)}
                            title="Remove line"
                          >
                            <i className="fa fa-trash"></i>
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <th colSpan="3" className="bp-num">
                      Total Budget
                    </th>
                    <th className="bp-num">{money(totalAmount)}</th>
                    <th></th>
                  </tr>
                </tfoot>
              </table>
            </div>
            <div className="bp-panel-body pd-t-14">
              {displayError("total_amount")}
              <div className="d-flex justify-content-between align-items-center">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={addMoreList}
                >
                  <i className="fa fa-plus mg-r-6"></i>Add Line
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={formSubmit}
                  disabled={saving}
                >
                  <i className="fa fa-send mg-r-10"></i>
                  {saving ? "Saving..." : "Create Plan"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateBudgetPlan;
